// Routing a tapped push notification, when the service worker could not do it.
//
// `notificationclick` in public/push-sw.js focuses an open window and calls
// `client.navigate(url)`. That is the whole story in Chrome and Firefox. Where
// navigate() is missing or refuses, the worker falls back to a postMessage — and
// this is the listener on the other end of it, mounted ONCE in App.tsx so no
// screen has to remember to handle it.
//
// Without this, tapping a notification on such a browser focuses the app and
// leaves the operator on whatever screen they were last on, which reads exactly
// like the notification doing nothing.

import { useEffect } from 'react'
import { useNavigate } from 'react-router'

export function useNotificationRouting(): void {
  const navigate = useNavigate()

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; url?: unknown } | null
      if (!data || data.type !== 'notification-click') return
      if (typeof data.url !== 'string') return

      // Same-origin, in-app paths ONLY. This message arrives through the service
      // worker from a push payload, so treating it as a navigation target
      // without checking would let anything that could reach the push service
      // steer the app. A leading single slash is the whole rule: '//evil.com'
      // is protocol-relative and would leave the origin.
      if (!data.url.startsWith('/') || data.url.startsWith('//')) return
      navigate(data.url)
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => { navigator.serviceWorker.removeEventListener('message', onMessage) }
  }, [navigate])
}
